<template>
  <div class="xy-raffle" ref="componentRef">
    <!-- 抽奖组件 -->
    <flip-raffle
      ref="raffleRef"
      :width="raffleCfg.width"
      :height="raffleCfg.height"
      :prizes="raffleCfg.prizes"
      :defaultStyle="raffleCfg.defaultStyle"
      :activeStyle="raffleCfg.activeStyle"
      :defaultConfig="raffleCfg.defaultConfig"
      :initFlippedCards="raffleCfg.initFlippedCards"
      @clip="(index) => startRaffle({ index })"></flip-raffle>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { info, defaultProps } from './config'
import { useRaffle } from '@/packages/components/hook/useRaffle'
import { computeRatioPx, computeRatio } from '@/packages/plugins/utils'
import flipRaffle from './components/flip-raffle.vue'
import useAppStore from '@/h5/stores/app'

defineOptions({ name: info.name })
const props = defineProps(defaultProps)
const appStore = useAppStore ? useAppStore() : null

// 抽奖配置
const raffleCfg = ref({
  width: '',
  height: '',
  prizes: [],
  bigReward: null,
  defaultStyle: {
    background: '#bac5ee'
  },
  activeStyle: {
    background: ''
  },
  defaultConfig: {
    cols: 5,
    verticalGutter: 5,
    horizontalGutter: 5
  },
  initFlippedCards: []
})

const initRaffle = () => {
  // 抽奖区域
  raffleCfg.value.width = computeRatioPx(props.width || 0)
  raffleCfg.value.height = computeRatioPx(props.height || 0)
  // 卡牌
  raffleCfg.value.defaultStyle.cardWidth = computeRatioPx(props.cardWidth || 0)
  raffleCfg.value.defaultStyle.cardHeight = computeRatioPx(props.cardHeight || 0)
  raffleCfg.value.defaultStyle.background = props.cardBackgroundColor || 'rgba(0,0,0,0)'
  raffleCfg.value.defaultStyle.backgroundImage = props.cardBackgroundSrc ? `url('${props.cardBackgroundSrc}')` : ''
  raffleCfg.value.defaultStyle.borderRadius = props.cardBorderRadius ? computeRatioPx(props.cardBorderRadius) : 0
  raffleCfg.value.activeStyle.background = props.cardActiveBackgroundColor || ''
  raffleCfg.value.activeStyle.backgroundImage = props.cardActiveBackgroundSrc ? `url('${props.cardActiveBackgroundSrc}')` : ''
  // 间距
  raffleCfg.value.defaultConfig.cols = props.cols
  raffleCfg.value.defaultConfig.verticalGutter = computeRatio(Number(props.verticalGutter))
  raffleCfg.value.defaultConfig.horizontalGutter = computeRatio(Number(props.horizontalGutter))
}

/* 设置奖项 */
const setPrizes = (raffleData, language, grandPrizeID) => {
  const prizes = []
  // 判断是否填写了大奖ID，填写了的话找出对应大奖消耗
  if (grandPrizeID) {
    const existReward = raffleData.bigReward.find((reward) => reward.id === grandPrizeID)
    raffleCfg.value.bigReward = existReward
  }
  // 根据传入的奖项设置prizes,并移除多余大奖配置
  for (let i = 0; i < raffleData.num; i++) {
    // 默认配置
    const prize = {
      text: props.cardText,
      color: props.cardTextColor,
      src: '',
      top: 0,
      width: computeRatioPx(80),
      height: computeRatioPx(80),
      isBig: false,
      srcVo: [],
      fontSize: computeRatioPx(14),
      fontColor: '#000000',
      fontPositionX: 0,
      fontPositionY: 0
    }
    prizes.push(prize)
  }
  raffleCfg.value.prizes = prizes
}

/* 根据抽奖结果设置翻牌奖项 */
const setPrizesResult = (resultList, indexArr, language) => {
  if (resultList.rewardList?.length) {
    resultList.rewardList.forEach((reward) => {
      const index = reward.index - 1
      const prizeItem = props.prizes.find((prize) => prize.id === reward.rewardId)
      const rafflePrizeItem = raffleCfg.value.prizes[index]
      if (prizeItem && rafflePrizeItem) {
        rafflePrizeItem.src = findPrizeImg(prizeItem, language) || ''
        rafflePrizeItem.isBig = prizeItem.isBig
        const rewardId = `${reward.itemVoList[0].type},${reward.itemVoList[0].contentId}`
        if (props.prizeImages[rewardId]) {
          const prizeImage = props.prizeImages[rewardId]
          rafflePrizeItem.top = computeRatioPx(prizeImage.top)
          rafflePrizeItem.width = computeRatioPx(prizeImage.width)
          rafflePrizeItem.height = computeRatioPx(prizeImage.height)
          rafflePrizeItem.fontSize = computeRatioPx(prizeImage.fontSize)
          rafflePrizeItem.fontColor = prizeImage.fontColor
          rafflePrizeItem.fontPositionX = computeRatioPx(prizeImage.fontPositionX)
          rafflePrizeItem.fontPositionY = computeRatioPx(prizeImage.fontPositionY)
        }
        // 正常只会有一个奖励，或有两个奖励，一个奖励是代币，并且不设置代币图片不展示代币的奖励图
        rafflePrizeItem.srcVo = findPrizeItemVoImg(reward, language)
      }
    })
  }
  indexArr && (raffleCfg.value.initFlippedCards = [...indexArr])
}

/* 查找奖励项的图片配置 */
const findPrizeImg = (item, language) => {
  const languageItem = item.languages.find((item) => item.language === language)
  let src = languageItem?.src
  if (languageItem.srcFeMale && languageItem.srcMale) {
    src = appStore?.pageDataSource['data-source_userConstant']?.sex === 2 ? languageItem.srcFeMale : languageItem.srcMale
  } else if (languageItem.srcMale) {
    src = languageItem.srcMale
  }
  return src
}

/* 查找奖品项的图片配置 */
const findPrizeItemVoImg = (item, language) => {
  const voList = []
  item?.itemVoList?.length &&
    item?.itemVoList?.forEach((itemVo) => {
      const key = `${itemVo.type},${itemVo.contentId}`
      if (props.prizeImages[key]) {
        let src = props.prizeImages[key].languages.find((item) => item.language === language)?.src
        if (src) {
          voList.push({ src, num: itemVo.count })
        }
      }
    })
  return voList
}

const { componentRef, raffleRef, chooseBigReward, startAll, startRaffle, reset, resetAll, getConfig, getMoneyBalances } = useRaffle(
  props,
  initRaffle,
  setPrizes,
  setPrizesResult,
  findPrizeImg,
  findPrizeItemVoImg
)

defineExpose({
  startAll,
  resetAll,
  reset,
  startRaffle,
  getConfig,
  chooseBigReward,
  getMoneyBalances
})
</script>

<style lang="scss" scoped>
.xy-raffle {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}
</style>
